import React, { FC } from 'react';
import { Button, Theme } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

interface StyleProps {
  shallow?: boolean;
}

const baseStyles = createStyles({
  base: {
    borderRadius: '3px',
    fontWeight: 'bold',
    letterSpacing: 0,
    opacity: 1,
  },
});

const buttonStyles = createStyles({
  primary: (props: StyleProps) => ({
    ...baseStyles.base,
    backgroundColor: '#FCA106',
    color: '#FFFFFF',
    boxShadow: props.shallow
      ? '0px 2px 4px #00000029'
      : '0px 2px 10px #00000029',
    fontSize: props.shallow ? '14px' : '16px',
    lineHeight: props.shallow ? '21px' : '24px',
    '&:hover': { backgroundColor: '#FEAD29' },
    '&:active': { backgroundColor: '#DE8C00' },
    '&:focus': { backgroundColor: '#FEAD29' },
    '&.Mui-disabled': { color: '#FFFFFF', opacity: 0.5 },
  }),
  secondary: {
    ...baseStyles.base,
    background: 'transparent',
    color: '#FFFFFF',
    border: '1px solid #FFFFFF',
    fontSize: '16px',
    lineHeight: '24px',
    '&:hover': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #0EC6BD',
      color: '#0EC6BD',
    },
    '&:active': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #0EC6BD',
      color: '#0EC6BD',
    },
    '&:focus': {
      backgroundColor: '#FFFFFF',
      border: '1px solid #0EC6BD',
      color: '#0EC6BD',
    },
    '&.Mui-disabled': { color: '#FFFFFF', opacity: 0.5 },
  },
  outlined: {
    ...baseStyles.base,
    backgroundColor: '#FFFFFF',
    color: '#FCA106',
    border: '1px solid #FCA106',
    fontSize: '16px',
    lineHeight: '24px',
    '&:hover': { backgroundColor: '#EDEFF3' },
    '&:active': { backgroundColor: '#F2F3FB66' },
    '&:focus': { backgroundColor: '#EDEFF3' },
    '&.Mui-disabled': { color: '#FFFFFF', opacity: 0.5 },
  },
  navigation: (props: StyleProps) => ({
    ...baseStyles.base,
    backgroundColor: '#FFFFFF',
    color: '#0EC6BD',
    borderRadius: '6px',
    boxShadow: props.shallow
      ? '0px 2px 6px #0000004D'
      : '0px 8px 16px #0000004D',
    fontSize: '16px',
    lineHeight: '24px',
    '&:hover': {
      backgroundColor: '#EDEFF3',
      '&.Mui-disabled': { backgroundColor: '#FFFFFF' },
    },
    '&:active': { backgroundColor: '#F2F3FB66' },
    '&:focus': { backgroundColor: '#EDEFF3' },
    '&.Mui-disabled': { color: '#0EC6BD', opacity: 1 },
  }),
  switch: (props: StyleProps) => ({
    ...baseStyles.base,
    backgroundColor: '#EDEFF3',
    color: '#7F7F7F',
    borderRadius: '6px',
    boxShadow: props.shallow
      ? '0px 2px 6px #0000004D'
      : '0px 8px 16px #0000004D',
    fontSize: '16px',
    lineHeight: '24px',
    '&:hover': {
      backgroundColor: '#EDEFF3',
    },
    '&:active': { backgroundColor: '#F2F3FB66' },
    '&:focus': { backgroundColor: '#EDEFF3' },
    '&.Mui-disabled': { color: '#FFFFFF', opacity: 0.5 },
  }),
});

const useStyles = makeStyles<Theme, StyleProps>(buttonStyles);

export type ButtonVariantType = keyof typeof buttonStyles;

export interface StyledButtonProps extends StyleProps {
  variant?: ButtonVariantType;
  label?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const StyledButton: FC<StyledButtonProps> = ({
  variant = 'primary',
  label = 'button',
  shallow,
  className,
  disabled = false,
  ...props
}) => {
  const classes = useStyles({ shallow: shallow });

  return (
    <Button
      className={`${classes[variant]} ${className}`}
      disabled={disabled}
      {...props}
    >
      {label}
    </Button>
  );
};
